import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonComponent } from '../button/button.component';
import { IconComponent } from '../../../icons/icon.component';
import type { NavIconName } from '../../../icons/nav-icon.model';

/**
 * MG3P Empty State — ilustração SVG, título, mensagem e ação opcional.
 */
@Component({
  selector: 'mg3p-empty-state',
  standalone: true,
  imports: [CommonModule, ButtonComponent, IconComponent],
  template: `
    <div class="mg3p-empty" [class]="'mg3p-empty--' + size">
      @if (iconName || icon) {
        <div class="mg3p-empty__illustration" aria-hidden="true">
          @if (iconName) {
            <mg3p-icon [name]="iconName!" [size]="illustrationIconSize()" />
          } @else {
            <span class="mg3p-empty__icon-legacy">{{ icon }}</span>
          }
        </div>
      }

      @if (imageSrc) {
        <img
          [src]="imageSrc"
          [alt]="imageAlt || ''"
          class="mg3p-empty__image"
        />
      }

      @if (title) {
        <h3 class="mg3p-empty__title">{{ title }}</h3>
      }

      @if (message) {
        <p class="mg3p-empty__message">{{ message }}</p>
      }

      @if (actionLabel) {
        <div class="mg3p-empty__actions">
          <mg3p-button
            [variant]="actionVariant"
            [size]="actionSize"
            (clicked)="handleAction()"
          >
            {{ actionLabel }}
          </mg3p-button>

          @if (secondaryActionLabel) {
            <mg3p-button
              variant="ghost"
              [size]="actionSize"
              (clicked)="handleSecondaryAction()"
            >
              {{ secondaryActionLabel }}
            </mg3p-button>
          }
        </div>
      }

      <ng-content></ng-content>
    </div>
  `,
  styles: [`
    .mg3p-empty {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      text-align: center;
      padding: var(--space-xl);
    }

    .mg3p-empty--sm {
      padding: var(--space-md) var(--space-lg);
    }

    .mg3p-empty--md {
      padding: var(--space-xl);
    }

    .mg3p-empty--lg {
      padding: var(--space-3xl);
    }

    .mg3p-empty__illustration {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 4.5rem;
      height: 4.5rem;
      margin-bottom: var(--space-md);
      border-radius: var(--radius-full);
      background: color-mix(in srgb, var(--color-pmdf-primary) 12%, var(--color-pmdf-surface));
      color: var(--color-pmdf-primary);
      border: 1px solid color-mix(in srgb, var(--color-pmdf-primary) 22%, transparent);
    }

    .mg3p-empty--sm .mg3p-empty__illustration {
      width: 3.25rem;
      height: 3.25rem;
      margin-bottom: var(--space-sm);
    }

    .mg3p-empty--lg .mg3p-empty__illustration {
      width: 5.5rem;
      height: 5.5rem;
      margin-bottom: var(--space-lg);
    }

    .mg3p-empty__illustration .mg3p-icon--lg {
      width: 2rem;
      height: 2rem;
    }

    .mg3p-empty--lg .mg3p-empty__illustration .mg3p-icon--lg {
      width: 2.5rem;
      height: 2.5rem;
    }

    .mg3p-empty__icon-legacy {
      font-size: 2rem;
      line-height: 1;
    }

    .mg3p-empty__image {
      max-width: 240px;
      height: auto;
      margin-bottom: var(--space-lg);
      opacity: 0.9;
    }

    .mg3p-empty__title {
      margin: 0 0 var(--space-sm);
      font-size: var(--font-size-xl);
      font-weight: var(--font-weight-semibold);
      color: var(--color-text-primary);
      max-width: 480px;
    }

    .mg3p-empty--sm .mg3p-empty__title {
      font-size: var(--font-size-md);
    }

    .mg3p-empty--lg .mg3p-empty__title {
      font-size: var(--font-size-2xl);
    }

    .mg3p-empty__message {
      margin: 0 0 var(--space-lg);
      font-size: var(--font-size-base);
      color: var(--color-text-secondary);
      line-height: var(--line-height-relaxed);
      max-width: 560px;
    }

    .mg3p-empty--sm .mg3p-empty__message {
      font-size: var(--font-size-sm);
      margin-bottom: var(--space-md);
    }

    .mg3p-empty__actions {
      display: flex;
      flex-wrap: wrap;
      gap: var(--space-sm);
      justify-content: center;
    }
  `],
})
export class EmptyStateComponent {
  /** @deprecated Preferir iconName */
  @Input() icon?: string;

  @Input() iconName?: NavIconName;

  @Input() imageSrc?: string;
  @Input() imageAlt?: string;
  @Input() title?: string;
  @Input() message?: string;
  @Input() actionLabel?: string;
  @Input() actionVariant: 'primary' | 'secondary' | 'danger' | 'ghost' = 'primary';
  @Input() actionSize: 'sm' | 'md' | 'lg' = 'md';
  @Input() secondaryActionLabel?: string;
  @Input() size: 'sm' | 'md' | 'lg' = 'md';

  @Output() action = new EventEmitter<void>();
  @Output() secondaryAction = new EventEmitter<void>();

  illustrationIconSize(): 'md' | 'lg' {
    return this.size === 'lg' ? 'lg' : 'md';
  }

  handleAction(): void {
    this.action.emit();
  }

  handleSecondaryAction(): void {
    this.secondaryAction.emit();
  }
}
